import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Typography,
  TextField,
  Chip,
  Switch,
  Button,
  FormControlLabel,
  Checkbox,
  Grid,
  Paper,
  CircularProgress
} from "@mui/material";
import { isEmpty } from "../../../utils/Util";
import {
  CreateSurchargeProfile,
  SurchargeProfileResponse,
  UpdateSurchargeProfile
} from "../../../types/types";
import {
  useGetCountryStatesQuery,
  useGetCountriesQuery
} from "../../../services/API/commonApiSlice";
import { FormStyleCollection as formStyles } from "../../../styles/form";
import { useLocation } from "react-router-dom";
import {
  useCreateNewSurchargeProfileMutation,
  useUpdateSurchargeProfileMutation,
  useDeleteSurchargeProfileMutation
} from "../../../services/API/surchargeApiSlice";
import { JWTDecode } from "../../../utils/UtilTarget";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import HtmlTooltip from "../../../common/components/htmltooltip";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

//*** For now, We're not using the payment methods from Database, because Adyen only supported 3 brands for the Surcharge at the moment! ***//
const brands = [
  {
    description: "Visa",
    code: "visa",
    priority: 1
  },
  {
    description: "Mastercard",
    code: "mc",
    priority: 2
  },
  {
    description: "Discover",
    code: "discover",
    priority: 3
  }
];

export default function SurchargeForm() {
  const [countryStates, setCountryStates] = React.useState<any>([]);
  const [countries, setCountries] = React.useState<any>([]);
  const [selectedCountry, setSelectedCountry] = React.useState<any>(null);
  const [selectedState, setSelectedState] = React.useState<any>(null);
  const [isEditMode, setIsEditMode] = React.useState<any>(false);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = React.useState<
    any
  >([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState<SurchargeProfileResponse | null>(
    null
  );
  const [surchargeValueMsg, setSurchargeValueMsg] = React.useState<any>(null);
  const [originalEditInfo, setOriginalEditInfo] = React.useState<any>(null);
  const [createSurcharge] = useCreateNewSurchargeProfileMutation();
  const [updateSurcharge] = useUpdateSurchargeProfileMutation();
  const [deleteSurcharge] = useDeleteSurchargeProfileMutation();
  const [isModalOpen, setModalOpen] = useState(false);
  let token = localStorage.getItem("access_token");
  let username = JWTDecode(token, "name");
  const location = useLocation();
  const navigate = useNavigate();
  const existingProfile = location?.state?.Profile;

  async function SetProfileFromLocation() {
    if (existingProfile) {
      setOriginalEditInfo(existingProfile);

      if (existingProfile.surchargeBrandConfig)
        existingProfile.surchargeValue =
          existingProfile.surchargeBrandConfig[0].surchargeValue;

      setFormData(existingProfile);

      if (existingProfile.surchargeBrandConfig) {
        var existingBrands = brands.filter(x =>
          existingProfile.surchargeBrandConfig.some(cfg => cfg.brand === x.code)
        );
        setBrandsInForm(existingBrands);
      }
      setIsEditMode(true);
    }
  }

  const {
    data: statesData,
    isFetching: isStatesFetching
  } = useGetCountryStatesQuery(
    selectedCountry
      ? {
          query: "CountryStates",
          queryBody: { Country: selectedCountry.Code }
        }
      : undefined,
    {
      skip: !selectedCountry
    }
  );

  const {
    data: countriesList,
    isFetching: isCountriesFetching
  } = useGetCountriesQuery({ refetchOnMountOrArgChange: true });

  useEffect(() => {
    setIsProcessing(true);
    document.title = "Setup Surcharge Profile";

    const getContent = async () => {
      await SetProfileFromLocation();
      if (!formData && !isEditMode) {
        setFormData(prev => ({
          ...prev,
          ["askConfirmation"]: true
        }));
      }
    };

    setCountries(countriesList);

    if (existingProfile) {
      if (!isCountriesFetching) {
        const country = countriesList.find(
          x => x.Code === existingProfile.countryCode
        );
        if (country) {
          setSelectedCountry(country);
        }
      }
    }

    getContent();
    setIsProcessing(false);
  }, [countriesList, isCountriesFetching]);

  useEffect(() => {
    if (!statesData) return;

    setCountryStates(statesData);

    // reset ONLY when creating a new profile
    if (!isEditMode) {
      setSelectedState(null);
      setFormData(prev => ({
        ...prev,
        stateCode: null
      }));
    }
  }, [statesData, isEditMode]);

  useEffect(() => {
    if (
      isEditMode &&
      statesData &&
      existingProfile?.stateCode &&
      !selectedState
    ) {
      const state = statesData.find(x => x.Code === existingProfile.stateCode);
      if (state) {
        setSelectedState(state);
      }
    }
  }, [statesData, isEditMode, existingProfile, selectedState]);

  const handleChange = (field: string) => (event: any) => {
    const value =
      field === "askConfirmation" ? event.target.checked : event.target.value;

    if (field === "surchargeValue") {
      var isValid = value >= 0.01 && value <= 4.0;
      if (!isValid) setSurchargeValueMsg("Invalid Input!");
      else setSurchargeValueMsg("");
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  function CheckData(): boolean {
    if (formData.name == null || formData.name == "") {
      toast.error("Invalid input. Missing  Profile Name");
      return false;
    }
    if (formData.countryCode == null || formData.countryCode == "") {
      toast.error("Invalid input. Missing Country");
      return false;
    }

    if (
      formData.surchargeValue == null ||
      formData.surchargeValue < 0.01 ||
      formData.surchargeValue > 4.0
    ) {
      toast.error(
        "Invalid input. Surcharge percentage should be between 0.01 - 4.00"
      );
      return false;
    }

    if (formData.surchargeBrandConfig?.length <= 0) {
      alert("Invalid input. Missing payment method");
      return false;
    }

    return true;
  }

  const handleCountrySelection = value => {
    setSelectedCountry(value);
    setSelectedState(null);
    setCountryStates([]);

    if (value) {
      setFormData(prev => ({
        ...prev,
        ["currency"]: value?.CurrencyCode,
        ["countryCode"]: value?.Code,
        ["stateCode"]: null
      }));
    }
  };

  const handleStateSelection = value => {
    setSelectedState(value);
    setFormData(prev => ({
      ...prev,
      ["stateCode"]: value?.Code
    }));
  };

  const handleBrandToggle = (brand: any) => {
    setBrandsInForm(brand);
  };

  const setBrandsInForm = (brand: any) => {
    setSelectedPaymentMethods(brand);
    setFormData(prev => {
      return {
        ...prev,
        surchargeBrandConfig: brand.map(x => ({
          brand: x.code,
          surchargeType: "Percentage",
          fundingSource: "Credit"
        }))
      };
    });
  };

  const handleSubmission = async () => {
    try {
      setIsProcessing(true);

      var validation = CheckData();
      if (validation) {
        if (isEditMode) await handleUpdate();
        else await handleCreate();
      }
    } catch (err) {
      toast.error("Failed to process the surcharge profile.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdate = async () => {
    const payload: UpdateSurchargeProfile = {
      id: formData.id,
      name: formData.name,
      description: formData.description,
      updatedBy: username
    };

    try {
      const response = await updateSurcharge(payload).unwrap();
      if (response !== undefined && response.id) {
        toast.success(`Surcharge profile updated successfully.`);
        setTimeout(() => {
          navigate("/dashboard/manageProfiles");
        }, 2000);
      }
    } catch (err) {
      handleApiError(err);
    }
    setIsProcessing(false);
  };

  const handleCreate = async () => {
    const payload: CreateSurchargeProfile = {
      name: formData.name,
      description: formData.description,
      countryCode: formData.countryCode,
      stateCode: formData.stateCode,
      currency: formData.currency,
      askConfirmation: formData.askConfirmation,
      surchargeValue: formData.surchargeValue,
      brandConfigs: formData.surchargeBrandConfig,
      updatedBy: username,
      createdBy: username
    };

    try {
      const response = await createSurcharge(payload).unwrap();
      console.log("Update successful:", response);
      if (response !== undefined && response.id) {
        toast.success(`Surcharge profile created successfully.`);
        setTimeout(() => {
          navigate("/dashboard/manageProfiles");
        }, 2000);
      }
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleSurchargeDelete = async () => {
    setIsProcessing(true);
    const id: string = formData?.id;
    try {
      const response = await deleteSurcharge(id).unwrap();
      console.log("Delete response:", response);
      if (response !== undefined && response?.id) {
        toast.success(
          "Surchare Profile " + response?.name + " Deleted Successfully!"
        );
        setTimeout(() => {
          navigate("/dashboard/manageProfiles");
        }, 2000);
      }
    } catch (err) {
      handleApiError(err);
      setIsProcessing(false);
    }
  };

  function handleApiError(error: any) {
    if (error?.status === HttpStatusCode.BadRequest && error.data?.errors) {
      const errors = error.data.errors;

      Object.entries(errors).forEach(([field, messages]) => {
        (messages as string[]).forEach(message => {
          toast.error(`${message}`, {
            autoClose: 5000
          });
        });
      });
    } else {
      toast.error("An unexpected error occurred.");
    }
  }

  return (
    <Box>
      <Paper
        elevation={3}
        sx={{
          maxWidth: 650,
          mx: "auto",
          px: 4,
          pt: 2,
          borderRadius: 3
        }}
      >
        <Typography variant="h6" color="primary" gutterBottom>
          Setup Surcharge Profile
        </Typography>
        <Box>
          <TextField
            label="Profile Name *"
            fullWidth
            margin="normal"
            variant="standard"
            value={formData?.name}
            onChange={handleChange("name")}
          />
          <TextField
            sx={{ mb: 3 }}
            label="Description"
            fullWidth
            multiline
            rows={3}
            margin="normal"
            variant="standard"
            value={formData?.description}
            onChange={handleChange("description")}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Autocomplete
                id="combo-box-demo"
                autoHighlight
                options={countries}
                disabled={isEditMode || isCountriesFetching ? true : false}
                value={selectedCountry}
                isOptionEqualToValue={(option, value) =>
                  option.Code === value.Code
                }
                getOptionLabel={(option: any) => {
                  let label = `${option.Name} (${option.Code})`;
                  return label;
                }}
                //getOptionDisabled={(option) => option.disabled}
                renderInput={params => (
                  <TextField
                    size="small"
                    variant="standard"
                    sx={{ fontSize: "small" }}
                    {...params}
                    label="Country *"
                    style={{ padding: "0px" }}
                    fullWidth
                  />
                )}
                onChange={(event, value) => {
                  handleCountrySelection(value || null);
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                id="combo-box-demo"
                autoHighlight
                options={countryStates}
                value={selectedState}
                disabled={
                  !selectedCountry || isEditMode || isStatesFetching
                    ? true
                    : false
                }
                getOptionLabel={(option: any) => {
                  let label = `${option.Name} (${option.Code})`;
                  return label;
                }}
                isOptionEqualToValue={(option, value) =>
                  option.Code === value.Code
                }
                renderInput={params => (
                  <TextField
                    size="small"
                    variant="standard"
                    sx={{ fontSize: "small" }}
                    {...params}
                    label="State/Province"
                    style={{ padding: "0px" }}
                    fullWidth
                  />
                )}
                onChange={(event, value) => {
                  handleStateSelection(value || null);
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6} mt={2}>
              <Box display="flex" alignItems="center" mt={2}>
                <Typography>
                  Ask confirmation
                  <HtmlTooltip
                    placement="top"
                    content={[
                      "Enabling this option triggers a confirmation prompt on the terminal. Disabling it suppresses the prompt, which is non-compliant with payment regulations."
                    ]}
                    iconStyles={{ color: "#1976d2", fontSize: "18px" }}
                  ></HtmlTooltip>
                </Typography>
                <Switch
                  checked={!formData ? true : formData?.askConfirmation}
                  onChange={handleChange("askConfirmation")}
                  disabled={isEditMode}
                  sx={{ ml: 2 }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              {selectedCountry && (
                <TextField
                  label="Currency *"
                  fullWidth
                  margin="normal"
                  variant="standard"
                  value={formData?.currency}
                  disabled
                />
              )}
            </Grid>
          </Grid>

          <TextField
            label="Surcharge Percentage (0.01% - 4.00%) *"
            inputProps={{
              step: "0.01" // allows decimal steps
            }}
            type="number"
            fullWidth
            margin="normal"
            variant="standard"
            value={formData?.surchargeValue}
            disabled={isEditMode}
            onChange={handleChange("surchargeValue")}
          />
          {surchargeValueMsg !== "" && (
            <Box
              style={{
                color: "red"
              }}
            >
              {surchargeValueMsg}
            </Box>
          )}

          <Box style={formStyles.fieldSpacer}>
            <Autocomplete
              fullWidth
              multiple
              id="combo-box-demo"
              disabled={isEditMode}
              disableCloseOnSelect
              options={brands}
              value={selectedPaymentMethods || []}
              getOptionLabel={option => option.code}
              onChange={(event, value) => handleBrandToggle(value || null)}
              renderOption={(props, option, { selected }) => {
                const isDisabled = selectedPaymentMethods.some(
                  element => element.code === option.code && isEditMode
                );

                return (
                  <li
                    {...props}
                    style={{
                      pointerEvents: isDisabled ? "none" : "auto"
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          style={{ padding: "2px 5px" }}
                          checked={selected}
                        />
                      }
                      label={option.description}
                    />
                  </li>
                );
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  //onKeyDown={handleKeyDownEvent}
                  variant="standard"
                  label="Payment Methods *"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: ""
                  }}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: true // Disable typing in the TextField
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.code === value.code
              }
              renderTags={(selected, getTagProps) =>
                selected.map((option, index) => {
                  const isDisabled = selectedPaymentMethods.some(
                    element => element.code === option.code && isEditMode
                  );

                  return (
                    <Chip
                      key={option.code}
                      label={option.code}
                      onDelete={
                        !isEditMode
                          ? getTagProps({ index }).onDelete
                          : undefined
                      }
                      {...getTagProps({ index })}
                      disabled={isDisabled}
                      style={{
                        pointerEvents: isDisabled ? "none" : "auto",
                        color: isDisabled ? "gray" : "inherit"
                      }}
                    />
                  );
                })
              }
            />
          </Box>

          <Box
            sx={{
              pt: 5,
              pb: 2
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSubmission()}
              disabled={
                !formData?.name ||
                !selectedCountry ||
                !formData?.surchargeValue ||
                formData?.surchargeBrandConfig?.length == 0 ||
                formData?.surchargeBrandConfig?.length == undefined ||
                (isEditMode &&
                  formData?.name == originalEditInfo?.name &&
                  formData?.description == originalEditInfo?.description) ||
                isProcessing
              }
            >
              {isEditMode ? "Update" : "SAVE"}
            </Button>
            {(isProcessing || isCountriesFetching || isStatesFetching) && (
              <CircularProgress
                size={25}
                style={{ marginLeft: "20px" }}
                color="info"
              />
            )}
            {isEditMode && (
              <>
                <Button
                  style={{ float: "right" }}
                  variant="outlined"
                  color="error"
                  onClick={() => setModalOpen(true)}
                >
                  Delete
                </Button>

                <ConfirmationModal
                  open={isModalOpen}
                  onClose={() => setModalOpen(false)}
                  heading="Confirm Deletion"
                  confirmationText="This surcharge profile will be permanently deleted. Do you wish to proceed?"
                  confirmationButtonText="Delete"
                  onConfirm={handleSurchargeDelete}
                />
              </>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
